import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Select, MenuItem, FormControl, InputLabel, TextField,
  IconButton, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import api from '../services/api';

// Imagem estática do mapa facial (frontal, dividida pele/musculatura)
const IMAGEM_ROSTO = '/rosto-estetico-anatomia.png';

const ORANGE = '#F97316';

const STATUS_CONFIG = {
  planejado:  { label: 'Planejado',    color: '#3b82f6', fill: '#eff6ff', stroke: '#3b82f6', textColor: '#2563eb' },
  realizado:  { label: 'Realizado',    color: '#22c55e', fill: '#f0fdf4', stroke: '#22c55e', textColor: '#16a34a' },
  reavaliar:  { label: 'Reavaliar',    color: '#f59e0b', fill: '#fffbeb', stroke: '#f59e0b', textColor: '#b45309' },
};

// Regiões clicáveis sobre a imagem — posição em % (x, y) relativa à largura/altura da imagem
const REGIOES = [
  { id: 'testa',                  label: 'Testa',                          x: 50, y: 16 },
  { id: 'glabela',                label: 'Glabela',                        x: 50, y: 28 },
  { id: 'temporas_esq',           label: 'Têmpora Esquerda',               x: 28, y: 22 },
  { id: 'temporas_dir',           label: 'Têmpora Direita',                x: 72, y: 22 },
  { id: 'olheira_esq',            label: 'Olheira Esquerda',               x: 40, y: 39 },
  { id: 'olheira_dir',            label: 'Olheira Direita',                x: 60, y: 39 },
  { id: 'malar_esq',              label: 'Malar Esquerdo (maçã do rosto)', x: 33, y: 47 },
  { id: 'malar_dir',              label: 'Malar Direito (maçã do rosto)',  x: 67, y: 47 },
  { id: 'nariz',                  label: 'Nariz',                          x: 50, y: 47 },
  { id: 'sulco_nasolabial_esq',   label: 'Sulco Nasolabial Esquerdo',      x: 41, y: 57 },
  { id: 'sulco_nasolabial_dir',   label: 'Sulco Nasolabial Direito',       x: 59, y: 57 },
  { id: 'labios',                 label: 'Lábios',                         x: 50, y: 64 },
  { id: 'bigode_chines_esq',      label: 'Linha de Marionete Esquerda',    x: 40, y: 70 },
  { id: 'bigode_chines_dir',      label: 'Linha de Marionete Direita',     x: 60, y: 70 },
  { id: 'mandibula_esq',          label: 'Mandíbula Esquerda',             x: 26, y: 73 },
  { id: 'mandibula_dir',          label: 'Mandíbula Direita',              x: 74, y: 73 },
  { id: 'papada',                 label: 'Papada',                         x: 50, y: 80 },
  { id: 'pescoco',                label: 'Pescoço',                        x: 50, y: 92 },
];

const PROCEDIMENTOS_PADRAO = [
  'Toxina Botulínica (Botox)',
  'Preenchimento com Ácido Hialurônico',
  'Bioestimulador de Colágeno',
  'Fios de Sustentação (PDO)',
  'Peeling Químico',
  'Laser / IPL',
  'Skinbooster',
  'Enzimas (Lipólise)',
  'Harmonização Facial',
  'Microagulhamento',
  'Ultrassom Microfocado (HIFU)',
];

const RostoEstetico = ({ pacienteId, pacienteNome, onAgendar }) => {
  const [dados, setDados] = useState({});
  const [modal, setModal] = useState(false);
  const [regiaoSelecionada, setRegiaoSelecionada] = useState(null);
  const [status, setStatus] = useState('');
  const [procedimento, setProcedimento] = useState('');
  const [obs, setObs] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [procedimentos, setProcedimentos] = useState([]);

  useEffect(() => {
    api.get(`/pacientes/${pacienteId}/estetica`)
      .then(r => setDados(r?.dados || r?.data?.dados || {}))
      .catch(() => {});
    api.get('/procedimentos')
      .then(r => {
        const lista = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
        setProcedimentos(lista);
      })
      .catch(() => {});
  }, [pacienteId]);

  const abrirModal = (regiao) => {
    setRegiaoSelecionada(regiao);
    const atual = dados[regiao.id];
    setStatus(atual?.status || '');
    setProcedimento(atual?.procedimento || '');
    setObs(atual?.obs || '');
    setModal(true);
  };

  const handleSalvar = async () => {
    if (!status) return;
    setSalvando(true);
    try {
      const res = await api.patch(`/pacientes/${pacienteId}/estetica`, {
        regiao: regiaoSelecionada.id, status, procedimento, obs,
      });
      setDados(res?.dados || res?.data?.dados || {});
      setModal(false);
    } catch {}
    setSalvando(false);
  };

  const handleLimpar = async () => {
    setSalvando(true);
    try {
      const res = await api.patch(`/pacientes/${pacienteId}/estetica`, {
        regiao: regiaoSelecionada.id, status: null,
      });
      setDados(res?.dados || res?.data?.dados || {});
      setModal(false);
    } catch {}
    setSalvando(false);
  };

  const handleAgendar = () => {
    setModal(false);
    if (onAgendar) onAgendar({ regiao: regiaoSelecionada.label, procedimento, pacienteNome });
  };

  const statusAtual = regiaoSelecionada ? dados[regiaoSelecionada.id] : null;

  const entradas = REGIOES
    .filter(r => dados[r.id])
    .map(r => ({ ...r, ...dados[r.id] }));

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, alignItems: 'start' }}>

        {/* Mapa facial */}
        <Box sx={{
          position: 'relative',
          maxWidth: 380,
          aspectRatio: '1 / 1',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'rgba(249,115,22,0.03)',
          border: '1px solid rgba(249,115,22,0.1)',
        }}>
          <Box
            component="img"
            src={IMAGEM_ROSTO}
            alt="Mapa facial estético"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>

        {/* Organização */}
        <Box sx={{
          borderRadius: 2, border: '1px solid rgba(249,115,22,0.12)', p: 2,
          minHeight: 380, display: 'flex', flexDirection: 'column',
        }}>
          <Typography fontWeight={700} fontSize="0.85rem" sx={{ mb: 1.5 }}>Organização</Typography>

          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel shrink>Selecionar região</InputLabel>
            <Select
              value=""
              label="Selecionar região"
              displayEmpty
              notched
              onChange={e => abrirModal(REGIOES.find(r => r.id === e.target.value))}
            >
              <MenuItem value="" disabled>Escolha uma região...</MenuItem>
              {REGIOES.map(r => (
                <MenuItem key={r.id} value={r.id}>
                  {r.label}{dados[r.id] ? ` — ${STATUS_CONFIG[dados[r.id].status]?.label}${dados[r.id].procedimento ? ` · ${dados[r.id].procedimento}` : ''}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {entradas.length === 0 ? (
            <Typography variant="caption" color="text.disabled">Nenhuma região registrada ainda — clique no rosto ou selecione acima.</Typography>
          ) : (
            <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', color: 'text.disabled', bgcolor: 'background.paper' }}>Região</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', color: 'text.disabled', bgcolor: 'background.paper' }}>Procedimento</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', color: 'text.disabled', bgcolor: 'background.paper' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entradas.map(e => (
                    <TableRow key={e.id} hover onClick={() => abrirModal(e)} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>{e.label}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{e.procedimento || '—'}</TableCell>
                      <TableCell>
                        <Chip label={STATUS_CONFIG[e.status]?.label} size="small"
                          sx={{ bgcolor: STATUS_CONFIG[e.status]?.fill, color: STATUS_CONFIG[e.status]?.textColor, fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>

      {/* Modal da região */}
      <Dialog open={modal} onClose={() => setModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={700}>{regiaoSelecionada?.label}</Typography>
            {statusAtual && (
              <Chip label={STATUS_CONFIG[statusAtual.status]?.label} size="small"
                sx={{ bgcolor: STATUS_CONFIG[statusAtual.status]?.fill, color: STATUS_CONFIG[statusAtual.status]?.textColor, fontWeight: 700, fontSize: '0.7rem', border: `1px solid ${STATUS_CONFIG[statusAtual.status]?.stroke}` }} />
            )}
          </Box>
          <IconButton size="small" onClick={() => setModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={e => setStatus(e.target.value)}>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <MenuItem key={key} value={key}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cfg.stroke }} />
                    {cfg.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel shrink>Procedimento</InputLabel>
            <Select value={procedimento} label="Procedimento" onChange={e => setProcedimento(e.target.value)} displayEmpty notched>
              <MenuItem value="">— Nenhum —</MenuItem>
              {procedimentos.map(p => (
                <MenuItem key={p.id} value={p.nome}>{p.nome}</MenuItem>
              ))}
              {PROCEDIMENTOS_PADRAO.map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth size="small" multiline rows={2}
            label="Observações" value={obs}
            onChange={e => setObs(e.target.value)}
            placeholder="Ex: assimetria leve, sugerir retorno em 15 dias..."
          />
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
          {statusAtual && (
            <Button size="small" color="error" onClick={handleLimpar} disabled={salvando} sx={{ mr: 'auto' }}>
              Limpar
            </Button>
          )}
          {procedimento && (
            <Button size="small" startIcon={<CalendarMonthIcon />} variant="outlined"
              onClick={handleAgendar}
              sx={{ borderColor: ORANGE, color: ORANGE }}
            >
              Agendar
            </Button>
          )}
          <Button size="small" onClick={() => setModal(false)}>Cancelar</Button>
          <Button size="small" variant="contained" onClick={handleSalvar} disabled={salvando || !status}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RostoEstetico;
